import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";

const day = (date) => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(date);
const dateLabel = (value) => value ? new Date(value.length === 10 ? value + "T12:00:00" : value).toLocaleString("pt-BR", value.length === 10 ? { dateStyle: "short" } : { dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo" }) : "";
const linkLabel = { sincronizado: "Sincronizado", estudio: "Na agenda do estúdio, sem duplicação", estudio_pendente: "Aguardando revisão da conexão do estúdio", excluido: "Bloco encerrado", erro: "Sincronização pendente", conflito: "Precisa de revisão" };
const safeLink = (url) => {
  try { const u = new URL(url); return u.protocol === "https:" && /(^|\.)google\.com$/.test(u.hostname) ? url : undefined; } catch { return undefined; }
};

export default function PersonalCalendarPanel() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(false);
  const [events, setEvents] = useState(null);
  const [cursor, setCursor] = useState("");
  const [links, setLinks] = useState([]);
  const [disconnect, setDisconnect] = useState(false);
  const [block, setBlock] = useState(null);
  const [range, setRange] = useState({ inicio: day(new Date()), fim: day(new Date(Date.now() + 7 * 86400000)) });
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    const { data } = await api.get("/agenda-pessoal/status");
    if (!mounted.current) return;
    setStatus(data);
    if (data.conectado) {
      const result = await api.get("/agenda-pessoal/vinculos");
      if (mounted.current) setLinks(result.data);
    } else { setLinks([]); setEvents(null); setCursor(""); }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load().catch((err) => { if (mounted.current) setError(apiError(err)); });
    return () => { mounted.current = false; };
  }, [load]);

  useEffect(() => {
    const result = params.get("agenda_pessoal");
    if (!["conectado", "cancelado", "erro"].includes(result)) return;
    if (result === "conectado") toast.success("Sua agenda foi conectada. Nenhum convite ou bloco foi criado ao conectar.");
    else if (result === "cancelado") toast.info("Autorização cancelada. A conexão do estúdio foi preservada.");
    else { const message = params.get("motivo") || "Não foi possível conectar sua agenda"; setError(message); toast.error(message); }
    const next = new URLSearchParams(params);
    next.delete("agenda_pessoal"); next.delete("motivo");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const run = async (fn) => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError("");
    try { await fn(); }
    catch (err) { if (mounted.current) { setError(apiError(err)); toast.error(apiError(err)); } }
    finally { inFlight.current = false; if (mounted.current) setBusy(false); }
  };
  const consult = async (more = false) => {
    const { data } = await api.get("/agenda-pessoal/eventos", { params: { ...range, page_token: more ? cursor : "" } });
    if (mounted.current) { setEvents((old) => more ? [...(old || []), ...data.items] : data.items); setCursor(data.nextPageToken); }
  };
  const sync = () => run(async () => {
    let next = "";
    let pending = 0;
    const seen = new Set();
    do {
      const { data } = await api.post("/agenda-pessoal/sincronizar", null, { params: { after: next } });
      pending += data.resultados.filter((r) => !["sincronizado", "estudio", "excluido"].includes(r.status)).length;
      next = data.next_cursor;
      if (next && seen.has(next)) throw new Error("Sincronização interrompida; atualize a tela para continuar");
      seen.add(next);
    } while (next && mounted.current);
    if (!mounted.current) return;
    toast.success(pending ? `Sincronização concluída com ${pending} pendência(s). Confira os vínculos.` : "Seus blocos estão sincronizados, sem convites.");
    await load();
    if (events) await consult();
  });

  if (status && !status.liberado) return null;
  return <section className="studio-card p-4 sm:p-6 space-y-4" data-testid="personal-calendar">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div><h2 className="text-xl font-semibold flex items-center gap-2"><CalendarDays size={22} /> Minha agenda</h2>
        <p className="text-sm text-muted-foreground mt-2">Sua conexão individual. Piloto para Felipe e Arthur, separado da Agenda e do Drive do estúdio.</p></div>
      {status?.conectado && <span className="rounded-full border border-emerald-500/40 px-3 py-1 text-sm">Conectada</span>}
    </div>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {!status && <Button variant="outline" disabled={busy} onClick={() => run(load)}>Verificar minha conexão</Button>}
    {status && !status.configurado && <p>A conexão Google precisa ser configurada no servidor. Nenhuma senha ou chave deve ser informada nesta tela.</p>}
    {status?.configurado && <div className="flex flex-wrap items-center gap-3">
      {status.email && <p className="text-sm break-all" data-testid="personal-email">{status.email}</p>}
      <Button disabled={busy} onClick={() => run(async () => {
        const { data } = await api.get("/agenda-pessoal/conectar");
        const url = new URL(data.authorization_url);
        if (url.origin !== "https://accounts.google.com") throw new Error("Endereço de autorização inválido");
        window.location.assign(url.href);
      })}>{status.conectado || status.reautorizar ? "Reconectar minha agenda" : "Conectar minha agenda Google"}</Button>
      {status.conectado && <Button disabled={busy} variant="outline" onClick={() => setDisconnect(true)}>Desconectar só minha agenda</Button>}
    </div>}
    <p className="text-sm text-muted-foreground">Somente você consulta seus compromissos. As atividades atribuídas à sua pessoa geram blocos sem convidados; prazos sem horário aparecem como dia inteiro. Convites a clientes continuam sob aprovação de Felipe na conexão do estúdio. Alterações manuais no Google não são importadas para as tarefas.</p>
    {status?.conectado && <>
      <div className="flex flex-wrap items-end gap-3">
        <div><Label htmlFor="personal-start">De</Label><Input id="personal-start" type="date" value={range.inicio} onChange={(e) => { setRange({ ...range, inicio: e.target.value }); setEvents(null); setCursor(""); }} /></div>
        <div><Label htmlFor="personal-end">Até (não inclui este dia)</Label><Input id="personal-end" type="date" value={range.fim} onChange={(e) => { setRange({ ...range, fim: e.target.value }); setEvents(null); setCursor(""); }} /></div>
        <Button disabled={busy} variant="outline" onClick={() => run(() => consult())}>Consultar minha agenda</Button>
      </div>
      {events?.length === 0 && <p>Nenhum compromisso neste período.</p>}
      <ul className="divide-y divide-border">{events?.map((event) => <li key={event.id} className="py-3 space-y-1">
        <p className="font-semibold break-words">{event.summary || "Compromisso"}</p>
        <p className="text-sm">{dateLabel(event.start?.dateTime || event.start?.date)}</p>
        {safeLink(event.htmlLink) && <a className="text-primary underline text-sm" href={event.htmlLink} target="_blank" rel="noreferrer">Abrir no Google Agenda</a>}
      </li>)}</ul>
      {cursor && <Button disabled={busy} variant="outline" onClick={() => run(() => consult(true))}>Mais compromissos</Button>}
      <div className="flex flex-wrap gap-3">
        <Button disabled={busy} onClick={sync}>Sincronizar minhas atividades</Button>
        <Button disabled={busy} variant="outline" onClick={() => setBlock({ chave: crypto.randomUUID(), titulo: "", data: day(new Date()), hora_inicio: "09:00", hora_fim: "10:00", notas: "", local: "" })}>Novo bloco pessoal</Button>
      </div>
      <h3 className="font-semibold">Meus vínculos · últimos 100</h3>
      {!links.length && <p className="text-sm text-muted-foreground">Nenhum vínculo ainda. Atribua atividades à sua pessoa e sincronize para iniciar.</p>}
      <ul className="divide-y divide-border">{links.map((link) => <li key={link.source_id} className="py-3 space-y-1">
        <p className="font-semibold break-words">{link.titulo}</p>
        <p className="text-sm">{dateLabel(link.data)} · {linkLabel[link.status] || "Pendente"}</p>
        {link.mensagem && <p className="text-sm text-muted-foreground">{link.mensagem}</p>}
        {safeLink(link.html_link) && <a className="text-primary underline text-sm" href={link.html_link} target="_blank" rel="noreferrer">Ver bloco no Google</a>}
      </li>)}</ul>
    </>}
    <AlertDialog open={disconnect} onOpenChange={setDisconnect}><AlertDialogContent>
      <AlertDialogHeader><AlertDialogTitle>Desconectar sua agenda?</AlertDialogTitle><AlertDialogDescription>Somente sua conexão individual será desligada. A conexão do estúdio, o Drive, as agendas dos colegas e os eventos existentes serão preservados. Blocos deixam de receber atualizações até você reconectar a mesma conta.</AlertDialogDescription></AlertDialogHeader>
      <AlertDialogFooter><AlertDialogCancel disabled={busy}>Voltar</AlertDialogCancel><Button disabled={busy} onClick={() => run(async () => { await api.delete("/agenda-pessoal/conexao"); setDisconnect(false); await load(); })}>Confirmar desconexão individual</Button></AlertDialogFooter>
    </AlertDialogContent></AlertDialog>
    <Dialog open={!!block} onOpenChange={(open) => { if (!open && !busy) setBlock(null); }}><DialogContent>
      <DialogHeader><DialogTitle>Novo bloco pessoal</DialogTitle><DialogDescription>Reserva na sua agenda principal, sem convidados. Horários de Brasília.</DialogDescription></DialogHeader>
      {block && <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); run(async () => {
        const { data } = await api.post("/agenda-pessoal/blocos", block);
        setBlock(null); await load();
        if (data.status !== "sincronizado") setError(data.mensagem || "Bloco salvo, mas a sincronização está pendente. Confira os vínculos.");
        else toast.success("Bloco criado sem convidados");
        if (events) await consult();
      }); }}>
        <Label htmlFor="personal-title">Título</Label><Input id="personal-title" required maxLength={200} value={block.titulo} onChange={(e) => setBlock({ ...block, titulo: e.target.value })} />
        <Label htmlFor="personal-date">Data</Label><Input id="personal-date" required type="date" value={block.data} onChange={(e) => setBlock({ ...block, data: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">{[["hora_inicio", "Início"], ["hora_fim", "Fim"]].map(([key, label]) => <div key={key}><Label htmlFor={`personal-${key}`}>{label}</Label><Input id={`personal-${key}`} type="time" value={block[key]} onChange={(e) => setBlock({ ...block, [key]: e.target.value })} /></div>)}</div>
        <Button type="submit" disabled={busy} className="w-full">Salvar sem convidados</Button>
      </form>}
    </DialogContent></Dialog>
  </section>;
}
